import { useTranslation } from "react-i18next";

import type { ImportPreviewRow } from "@/lib/admin-api";

/**
 * apps/web :: components/admin :: ImportPreviewTable
 *
 * Renders the first N parsed rows from a bulk-import preview. The
 * upper bound (50) keeps the DOM cheap on large CSVs; a footer
 * disclaimer surfaces "and N more not shown" when the input is
 * truncated. Wire shape mirrors the backend `ParsedRow` JSON.
 *
 * Long question_text is truncated visually via `line-clamp-2` so the
 * row height stays uniform — full text is never logged or sent to
 * analytics regardless.
 */
export interface ImportPreviewTableProps {
  rows: ImportPreviewRow[];
  /** Maximum rows to render. Defaults to 50. */
  limit?: number;
}

const DEFAULT_LIMIT = 50;

export default function ImportPreviewTable({
  rows,
  limit = DEFAULT_LIMIT,
}: ImportPreviewTableProps) {
  const { t } = useTranslation();
  const visible = rows.slice(0, limit);
  const hidden = rows.length - visible.length;

  if (rows.length === 0) {
    return (
      <p
        data-testid="admin-import-preview-empty"
        className="rounded-md border border-neutral-200 bg-white p-md text-sm text-neutral-700"
      >
        {t("admin.import.preview.empty")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="admin-import-preview-table">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-700">
          <tr>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.row")}</th>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.state")}</th>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.topic")}</th>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.difficulty")}</th>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.question")}</th>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.correct")}</th>
            <th className="px-sm py-xs">{t("admin.import.preview.cols.locale")}</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr
              key={row.rowNumber}
              data-testid={`admin-import-preview-row-${row.rowNumber}`}
              className="border-t border-neutral-200 align-top"
            >
              <td className="px-sm py-xs text-neutral-700">{row.rowNumber}</td>
              <td className="px-sm py-xs font-mono text-xs">{row.state}</td>
              <td className="px-sm py-xs">{row.topicCode}</td>
              <td className="px-sm py-xs">{row.difficulty}</td>
              <td
                className="max-w-md px-sm py-xs"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {row.questionText}
              </td>
              <td className="px-sm py-xs text-neutral-700">{row.correctIndex}</td>
              <td className="px-sm py-xs font-mono text-xs">{row.locale}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && (
        <p
          data-testid="admin-import-preview-truncated"
          className="mt-sm text-xs text-neutral-600"
        >
          {t("admin.import.preview.truncated", { count: hidden })}
        </p>
      )}
    </div>
  );
}
